/*  
This component prints the user data (name, email, uid) in table format. This component fetches data from
the /allUsers API endpoint and displays it in a sortable table format.

This component calls Updatedtime component (which prints last fetched data's date and time), and includes
a refresh functionality. It has table heading and table data with sort function implemented (sorts based on 
arrow clicks - ▲ for ascending order, ▼ for descending order).

Updated to include search functionality and PDF download capability.
Enhanced with search text highlighting functionality.
*/

import React, { useState, useEffect } from "react";
import './HarnessUser.css';
import Updatedtime from '../UpdatedTime/Updatedtime';
import VersionDownload from '../Download/VersionDownload';
import loader from "../../assets/loader.gif";
import { FaSearch, FaTimes } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const HarnessUser = () => {
    const [userData, setUserData] = useState([]);
    const [filteredUserData, setFilteredUserData] = useState([]);
    const [userTime, setUserTime] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const [totalUsers, setTotalUsers] = useState(0);
    const [activeUsers, setActiveUsers] = useState(0);
    const [avalLicense, setAvalLicense] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    // Function to highlight search text
    const highlightText = (text, searchTerm) => {
        if (!searchTerm || !text) return text;
        
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => {
            if (part.toLowerCase() === searchTerm.toLowerCase()) {
                return (
                    <span
                        key={index}
                        style={{
                            backgroundColor: 'rgb(25, 82, 25)',
                            color: 'white',
                            padding: '2px 4px',
                            borderRadius: '3px',
                            fontWeight: 'bold'
                        }}
                    >
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${API_BASE_URL}/allUsers`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                console.log('Initial fetch response:', data);

                // Extract users from the nested data structure
                const users = data.data?.users || [];
                const formattedUsers = users.map(item => ({
                    name: item.user?.name || 'N/A',
                    email: item.user?.email || 'N/A',
                    uid: item.user?.uuid || 'N/A',
                    active: item.user?.active || false,
                    disabled: item.user?.disabled || false,
                    locked: item.user?.locked || false,
                    status: item.user?.locked ? 'Locked' : item.user?.disabled ? 'Disabled' : item.user?.active ? 'Active' : 'Inactive'
                }));

                setUserData(formattedUsers);
                setFilteredUserData(formattedUsers);
                setTotalUsers(data.data?.totalUsers || 0);
                setActiveUsers(data.data?.activeUsersCount || 0);
                setAvalLicense(50 - data.data?.totalUsers || 0);

                // Use retrievedAt timestamp or current time
                if (data.data?.retrievedDate && data.data?.retrievedTime) {
                    setUserTime(`${data.data.retrievedDate} ${data.data.retrievedTime}`);
                } else {
                    setUserTime(new Date().toISOString());
                }
            } catch (err) {
                console.error('Fetch error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // Filter users based on search term
    useEffect(() => {
        if (!searchTerm) {
            setFilteredUserData(userData);
        } else {
            const filtered = userData.filter(user =>
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) 
                // user.uid.toLowerCase().includes(searchTerm.toLowerCase()) ||
                // user.status.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUserData(filtered);
        }
    }, [searchTerm, userData]);

    const handleSort = (key, direction) => {
        const sortedData = [...filteredUserData].sort((a, b) => {
            const aValue = (a[key] || '').toString().toLowerCase();
            const bValue = (b[key] || '').toString().toLowerCase();

            if (direction === 'asc') {
                return aValue.localeCompare(bValue);
            } else {
                return bValue.localeCompare(aValue);
            }
        });

        setFilteredUserData(sortedData);
        setSortConfig({ key, direction });
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    const refreshUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/allUsers`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Refresh response:', data);

            const users = data.data?.users || [];
            const formattedUsers = users.map(item => ({
                name: item.user?.name || 'N/A',
                email: item.user?.email || 'N/A',
                uid: item.user?.uuid || 'N/A',
                active: item.user?.active || false,
                disabled: item.user?.disabled || false,
                locked: item.user?.locked || false,
                status: item.user?.locked ? 'Locked' : item.user?.disabled ? 'Disabled' : item.user?.active ? 'Active' : 'Inactive'
            }));

            setUserData(formattedUsers);
            setFilteredUserData(formattedUsers);
            setTotalUsers(data.data?.totalUsers || 0);
            setActiveUsers(data.data?.activeUsersCount || 0);
            setAvalLicense(50 - data.data?.totalUsers || 0);

            if (data.data?.retrievedDate && data.data?.retrievedTime) {
                setUserTime(`${data.data.retrievedDate} ${data.data.retrievedTime}`);
            } else {
                setUserTime(new Date().toISOString());
            }
        } catch (err) {
            console.error('Refresh error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Configuration for PDF download
    const downloadColumns = ['Name', 'Email'];
    const downloadColumnKeys = ['name', 'email'];

    return (
        <div className="table-container-user">
            <div className="time-container-user">
                <div className="user-stats">
                    <div className="user-count">Total Users: {totalUsers}</div>
                    <div className="active-count">Available Licenses: {avalLicense}</div>
                    {/* <span className="active-count">Active Users: {activeUsers}</span>
                    <span className="filtered-count">
                        {searchTerm && `Filtered: ${filteredUserData.length}`}
                    </span> */}
                </div>
                <Updatedtime
                    environmentTime={userTime}
                    loading={loading}
                    error={error}
                />
                <div className="action-buttons">
                    <button
                        onClick={refreshUsers}
                        className="refresh-button-user"
                        data-tooltip="Refresh"
                    >
                        ↻
                    </button>
                    <VersionDownload
                        versionData={filteredUserData}
                        versionTime={userTime}
                        versionTableTitle="Harness Users"
                        columns={downloadColumns}
                        columnKeys={downloadColumnKeys}
                        filenamePrefix="harness_users"
                        userCount={totalUsers}
                    // activeUserCount={activeUsers}
                    />
                </div>
            </div>

            {/* Search Bar */}
            <div className="search-container-user">
                <div className="search-input-wrapper">
                    {/* <FaSearch className="search-icon" /> */}
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="search-input-user"
                    />
                    {searchTerm && (
                        <button
                            onClick={clearSearch}
                            className="clear-search-button"
                            title="Clear search"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            <div className="user-table-div">
                <table className="user-table">
                    <thead className="user-table-thead">
                        <tr>
                            <th className="user-th">
                                <div className="table-header-content-user">
                                    <span className="table-heading-user-name">Name</span>
                                    <div className="sort-arrows-user">
                                        <span
                                            onClick={() => handleSort('name', 'asc')}
                                            className={`arrow-user ${sortConfig.key === 'name' && sortConfig.direction === 'asc' ? 'active' : ''}`}
                                        >
                                            ▲
                                        </span>
                                        <span
                                            onClick={() => handleSort('name', 'desc')}
                                            className={`arrow-user ${sortConfig.key === 'name' && sortConfig.direction === 'desc' ? 'active' : ''}`}
                                        >
                                            ▼
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th className="user-th">
                                <div className="table-header-content-user">
                                    <span className="table-heading-user-email">Email</span>
                                    <div className="sort-arrows-user">
                                        <span
                                            onClick={() => handleSort('email', 'asc')}
                                            className={`arrow-user ${sortConfig.key === 'email' && sortConfig.direction === 'asc' ? 'active' : ''}`}
                                        >
                                            ▲
                                        </span>
                                        <span
                                            onClick={() => handleSort('email', 'desc')}
                                            className={`arrow-user ${sortConfig.key === 'email' && sortConfig.direction === 'desc' ? 'active' : ''}`}
                                        >
                                            ▼
                                        </span>
                                    </div>
                                </div>
                            </th>
                            {/* <th className="user-th">
                                <div className="table-header-content-user">
                                    <span className="table-heading-user-uid">UID</span>
                                    <div className="sort-arrows-user">
                                        <span
                                            onClick={() => handleSort('uid', 'asc')}
                                            className={`arrow-user ${sortConfig.key === 'uid' && sortConfig.direction === 'asc' ? 'active' : ''}`}
                                        >
                                            ▲
                                        </span>
                                        <span
                                            onClick={() => handleSort('uid', 'desc')}
                                            className={`arrow-user ${sortConfig.key === 'uid' && sortConfig.direction === 'desc' ? 'active' : ''}`}
                                        >
                                            ▼
                                        </span>
                                    </div>
                                </div>
                            </th> */}
                            {/* <th className="user-th">
                                <div className="table-header-content-user">
                                    <span className="table-heading-user-status">Status</span>
                                    <div className="sort-arrows-user">
                                        <span
                                            onClick={() => handleSort('status', 'asc')}
                                            className={`arrow-user ${sortConfig.key === 'status' && sortConfig.direction === 'asc' ? 'active' : ''}`}
                                        >
                                            ▲
                                        </span>
                                        <span
                                            onClick={() => handleSort('status', 'desc')}
                                            className={`arrow-user ${sortConfig.key === 'status' && sortConfig.direction === 'desc' ? 'active' : ''}`}
                                        >
                                            ▼
                                        </span>
                                    </div>
                                </div>
                            </th> */}
                        </tr>
                    </thead>
                    <tbody className="user-table-div-tbody">
                        {loading && (
                            <tr style={{ backgroundColor: '#f9f9f9' }}>
                                <td colSpan='4' className="status-message">
                                    <div className="loading">
                                        <img src={loader} alt="Loading..." />
                                    </div>
                                </td>
                            </tr>
                        )}
                        {error && (
                            <tr>
                                <td colSpan='4' className="status-message-error">Error: {error}</td>
                            </tr>
                        )}
                        {!loading && !error && filteredUserData.length === 0 && !searchTerm && (
                            <tr>
                                <td colSpan='4' className="status-message">No users found</td>
                            </tr>
                        )}
                        {!loading && !error && filteredUserData.length === 0 && searchTerm && (
                            <tr>
                                <td colSpan='4' className="status-message">No users match your search criteria</td>
                            </tr>
                        )}
                        {!loading && !error && filteredUserData.map((user, index) => (
                            <tr key={index} className="tr-user">
                                <td className="user-td" title={user.name}>
                                    {highlightText(user.name, searchTerm)}
                                </td>
                                <td className="user-td" title={user.email}>
                                    {highlightText(user.email, searchTerm)}
                                </td>
                                {/* <td className="user-td" title={user.uid}>{user.uid}</td> */}
                                {/* <td className="user-td">
                                    <span className={`status-badge ${user.active ? 'active' : 'inactive'} ${user.disabled ? 'disabled' : ''} ${user.locked ? 'locked' : ''}`}>
                                        {user.status}
                                    </span>
                                </td> */}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default HarnessUser;